import {colors, strokeWidth} from '../constants';

import {
  createPoint,
  buildGeometryInstance,
  getLayerForFlavoredEntityType,
  handleAddCorrectnessIcon
} from './entities.utils';

function getFocusAndDirectrix(vertex, curvePoint) {
  const [h, k] = vertex;
  const [a, b] = curvePoint;

  if (h === a || k === b) {
    // If the vertex and curve point share a coordinate, the parabola is
    // invalid and we return null
    return null;
  }

  const focusCoords = [h, k + Math.pow(a - h, 2) / (4 * (b - k))];

  const directrixYCoords = k - Math.pow(a - h, 2) / (4 * (b - k));
  const directrixCoords = [
    [-1, directrixYCoords],
    [1, directrixYCoords]
  ];

  return [focusCoords, directrixCoords];
}

const parabola = {
  /**
   * @example
   * const entity = {
   *   type: 'parabola',
   *   elements: {
   *     vertex: [-3, -3],
   *     curvePoint: [-3, -3]
   *   },
   *   flavor: 'studentGuess'
   * }
   */
  create({board, entity, onClick}) {
    const {
      elements: {vertex, curvePoint},
      flavor
    } = entity;

    const vertexElement = createPoint({board, coords: vertex, flavor});
    const curvePointElement = createPoint({
      board,
      coords: curvePoint,
      flavor
    });

    const focusAndDirectrix = getFocusAndDirectrix(vertex, curvePoint);

    const parabolaElement = board.create('parabola', focusAndDirectrix, {
      strokeWidth,
      highlightStrokeWidth: strokeWidth,
      layer: getLayerForFlavoredEntityType('curve', flavor),
      tabindex: null
    });

    const correctnessIcon = handleAddCorrectnessIcon({board, flavor, coords: vertex});

    return buildGeometryInstance({
      board,
      entity,
      geometryElement: parabolaElement,
      constituentElements: [parabolaElement, vertexElement, curvePointElement],
      onClick,
      correctnessIcon,
      onRemove: () => {
        board.removeAncestors(vertexElement);
        board.removeAncestors(curvePointElement);
      }
    });
  },
  createPartial({board, partialInstance, coords, flavor}) {
    if (!partialInstance) {
      return {
        entity: {
          type: 'parabola',
          elements: {
            vertex: null,
            curvePoint: null
          },
          flavor
        },
        geometryElements: [],
        done: false,
        remove() {
          return null;
        }
      };
    }

    const {
      entity: {
        elements: {vertex}
      },
      geometryElements
    } = partialInstance;

    const isValidNextCoord = (() => {
      if (!vertex) {
        return true;
      }
      const [vertexX, vertexY] = vertex;
      return coords[0] !== vertexX && coords[1] !== vertexY;
    })();

    if (!isValidNextCoord) {
      return null;
    }

    const nextPoint = createPoint({board, coords, flavor});

    const nextGeometryElements = [...geometryElements, nextPoint];

    const vertexCoords = vertex || coords;

    return {
      entity: {
        type: 'parabola',
        elements: {
          vertex: vertexCoords,
          curvePoint: vertex ? coords : null
        },
        flavor
      },
      geometryElements: nextGeometryElements,
      done: !!vertex, // If we had a vertex, we've just added the curvePoint, so we're done
      instructions: 'Complete the parabola by placing a second point.',
      handleCursorMove(cursorCoords) {
        if (board.elementsByName.previewParabola) {
          board.removeAncestors(board.elementsByName.previewParabola);
        }

        if (!vertexCoords || !cursorCoords) {
          return;
        }

        const focusAndDirectrix = getFocusAndDirectrix(vertexCoords, cursorCoords);

        if (!focusAndDirectrix) {
          return;
        }

        board.create('parabola', focusAndDirectrix, {
          name: 'previewParabola',
          strokeWidth,
          highlightStrokeWidth: strokeWidth,
          strokeColor: colors.preview,
          highlightStrokeColor: colors.preview,
          tabindex: null
        });
      },
      remove() {
        nextGeometryElements.forEach((element) => board.removeAncestors(element));
        return null;
      }
    };
  }
};

export default parabola;
